import {
  HoldingRowMetadata,
  HoldingRowData
} from '../components/dataTable/DataTable.d';
import { TABLE_HEADING_HOLDINGS, ASSET_NAME } from '../utils/constants';
import {
  formatCurrency,
  formatPercentage,
  formatNumber
} from './numberFormatter';
import { upperCase, startCase, toLower } from 'lodash';

const defaultHoldingMetadata = { isHeading: true, rowClass: '' };
const defaultSecurityMetadata = { isHeading: false, rowClass: '' };

function transformAssetTotal(
  assetName: string,
  assetTotal: HoldingDetail,
  totalOptions: HoldingRowMetadata = defaultHoldingMetadata,
  numericFunction: Function
): HoldingRowData[] {
  const totalRowData: any = {
    [TABLE_HEADING_HOLDINGS.NAME]: `TOTAL ${upperCase(assetName)}`,
    [TABLE_HEADING_HOLDINGS.COST]: formatCurrency(assetTotal.cost),
    [TABLE_HEADING_HOLDINGS.ANNUAL_INCOME]: formatCurrency(
      assetTotal.annualIncome
    ),
    [TABLE_HEADING_HOLDINGS.MARKET_YIELD_PERCENT]: formatPercentage(
      assetTotal.marketYieldPercent
    ),
    [TABLE_HEADING_HOLDINGS.MARKET_VALUE]: formatCurrency(
      assetTotal.marketValue
    ),
    [TABLE_HEADING_HOLDINGS.UNREALIZED_GAIN_LOSS]: numericFunction(
      assetTotal.unrealizedGainLoss
    ),
    [TABLE_HEADING_HOLDINGS.PERCENT_OF_PORTFOLIO]: formatPercentage(
      assetTotal.percentOfPortfolio
    ),
    [TABLE_HEADING_HOLDINGS.PERCENT_OF_ASSET_CLASS]:
      assetName !== ASSET_NAME.RESERVES
        ? formatPercentage(
            (assetTotal.percentOfPortfolio / assetTotal.percentOfPortfolio) *
              100
          )
        : null,
    ...defaultHoldingMetadata,
    ...totalOptions
  };
  return [totalRowData];
}

function transformSector(
  sector: Sector,
  sectorOptions: HoldingRowMetadata = defaultHoldingMetadata,
  numericFunction: Function,
  assetName: string,
  assetTotalPP: number
): HoldingRowData[] {
  // this has to be `any` to trick typescript
  const { securities, sectorName, sectorTotal } = sector;
  const sectorRowData: any = {
    [TABLE_HEADING_HOLDINGS.NAME]: startCase(toLower(sectorName)),
    [TABLE_HEADING_HOLDINGS.COST]: formatCurrency(sectorTotal.cost),
    [TABLE_HEADING_HOLDINGS.ANNUAL_INCOME]: formatCurrency(
      sectorTotal.annualIncome
    ),
    [TABLE_HEADING_HOLDINGS.MARKET_YIELD_PERCENT]: formatPercentage(
      sectorTotal.marketYieldPercent
    ),
    [TABLE_HEADING_HOLDINGS.MARKET_VALUE]: formatCurrency(
      sectorTotal.marketValue
    ),
    [TABLE_HEADING_HOLDINGS.UNREALIZED_GAIN_LOSS]: numericFunction(
      sectorTotal.unrealizedGainLoss
    ),
    [TABLE_HEADING_HOLDINGS.PERCENT_OF_PORTFOLIO]: formatPercentage(
      sectorTotal.percentOfPortfolio
    ),
    [TABLE_HEADING_HOLDINGS.PERCENT_OF_ASSET_CLASS]:
      assetName !== ASSET_NAME.RESERVES
        ? formatPercentage(
            (sectorTotal.percentOfPortfolio / assetTotalPP) * 100
          )
        : null,
    ...defaultHoldingMetadata,
    ...sectorOptions
  };

  const securitiesRowData = securities.map((security) =>
    transformSecurity(
      security,
      defaultSecurityMetadata,
      numericFunction,
      assetTotalPP,
      assetName
    )
  );
  return [sectorRowData, ...securitiesRowData];
}

function transformSecurity(
  holding: Security,
  options: HoldingRowMetadata = defaultSecurityMetadata,
  numericFunction: Function,
  sectorPortfolio: number,
  assetName: string
): HoldingRowData {
  const rowData: any = {
    [TABLE_HEADING_HOLDINGS.NAME]: holding.securityName,
    [TABLE_HEADING_HOLDINGS.QUANTITY]: formatNumber(holding.quantity),
    [TABLE_HEADING_HOLDINGS.COST]: formatCurrency(holding.cost),
    [TABLE_HEADING_HOLDINGS.ANNUAL_INCOME]: formatCurrency(
      holding.annualIncome
    ),
    [TABLE_HEADING_HOLDINGS.MARKET_YIELD_PERCENT]: formatPercentage(
      holding.marketYieldPercent
    ),
    [TABLE_HEADING_HOLDINGS.MARKET_PER_UNIT]: formatCurrency(
      holding.marketPerUnit
    ),
    [TABLE_HEADING_HOLDINGS.MARKET_VALUE]: formatCurrency(holding.marketValue),
    [TABLE_HEADING_HOLDINGS.UNREALIZED_GAIN_LOSS]: numericFunction(
      holding.unrealizedGainLoss
    ),
    [TABLE_HEADING_HOLDINGS.PERCENT_OF_PORTFOLIO]: formatPercentage(
      holding.percentOfPortfolio
    ),
    [TABLE_HEADING_HOLDINGS.PERCENT_OF_ASSET_CLASS]:
      assetName !== ASSET_NAME.RESERVES
        ? formatPercentage((holding.percentOfPortfolio / sectorPortfolio) * 100)
        : null,
    ...defaultSecurityMetadata,
    ...options
  };

  return rowData;
}

export { transformSector, transformAssetTotal };
